import { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  CalendarIcon,
  LayoutList,
  HelpCircle,
  FilterIcon,
} from "lucide-react";
import {
  format,
  addDays,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  parseISO,
  addWeeks,
  isWithinInterval,
  startOfDay,
  endOfDay,
} from "date-fns";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { ViewMode, ViewToggle } from "./ViewToggle";
import { MonthView } from "./MonthView";
import { WeekView } from "./WeekView";
import { DayView } from "./DayView";
import TicketTable from "../../admin/tickets/component";
import { TicketStatus } from "../../../services/LiveQueueService";
import { DatePickerWithRange } from "@/components/ui/DateRangePicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";

export type CalendarEvent = {
  id: string;
  title: string;
  start: Date | string;
  end: Date | string;
  status: TicketStatus;
  statusColor: string;
  user?: {
    username: string;
    name: string;
    avatar?: string;
  };
  ticket_number: string;
  description?: string;
};

export type CalendarViewType = "month" | "week";

interface CalendarProps {
  events: CalendarEvent[];
  initialView?: CalendarViewType;
  initialDate?: Date;
  onCreateEvent?: () => void;
  onViewEvent?: (event: CalendarEvent) => void;
  onFilterEvents?: () => void;
  onDateRangeChange?: (range: { start: Date; end: Date }) => void;
  onViewChange?: (view: CalendarViewType) => void;
  setView?: React.Dispatch<
    React.SetStateAction<"TicketAppoitment" | "list">
  >;
}

export function Calendar({
  events,
  initialView = "month",
  initialDate = new Date(), // Remove startOfDay() to avoid timezone issues
  onViewEvent,
  onFilterEvents,
  onDateRangeChange,
  onViewChange,
  setView,
}: CalendarProps) {
  // Initialize with proper handling of the date
  const [currentDate, setCurrentDate] = useState(() => {
    // Ensure we're working with the correct local date
    const date = initialDate || new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  });
  const [calendarView, setCalendarView] =
    useState<CalendarViewType>(initialView);
  const [viewMode, setViewMode] = useState<ViewMode>("calendar");
  const [filterDateRange, setFilterDateRange] = useState<DateRange | undefined>(
    undefined
  );

  // Get the visible date range based on current view
  const getVisibleDateRange = () => {
    if (calendarView === "month") {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const viewStart = startOfWeek(monthStart, { weekStartsOn: 0 });
      const viewEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
      return { start: viewStart, end: viewEnd };
    } else if (calendarView === "week") {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
      return { start: weekStart, end: weekEnd };
    } else {
      // Day view
      return { start: currentDate, end: currentDate };
    }
  };

  const dateRange = getVisibleDateRange();

  // Normalize events to ensure start and end are Date objects and filter by date range if set
  const normalizedEvents = events
    .map((event) => ({
      ...event,
      start: event.start instanceof Date ? event.start : parseISO(event.start),
      end: event.end instanceof Date ? event.end : parseISO(event.end),
    }))
    .filter((event) => {
      // If no filter date range is set, don't filter events
      if (!filterDateRange?.from) return true;

      const eventStart = new Date(event.start);

      if (filterDateRange.to) {
        // If we have a complete range, check if event is within range
        return isWithinInterval(eventStart, {
          start: startOfDay(filterDateRange.from),
          end: endOfDay(filterDateRange.to),
        });
      } else {
        // If we only have a start date, check if event is on that day
        return isWithinInterval(eventStart, {
          start: startOfDay(filterDateRange.from),
          end: endOfDay(filterDateRange.from),
        });
      }
    });

  useEffect(() => {
    const range = getVisibleDateRange();
    onDateRangeChange?.(range);
  }, [currentDate, calendarView]);

  // Modify setCalendarView to notify parent
  const handleViewChange = (view: CalendarViewType) => {
    setCalendarView(view);
    onViewChange?.(view);
  };

  // Update the navigation buttons to also update date range
  const handleNavigate = (direction: "prev" | "next") => {
    if (calendarView === "month") {
      setCurrentDate(
        direction === "prev"
          ? addMonths(currentDate, -1)
          : addMonths(currentDate, 1)
      );
    } else if (calendarView === "week") {
      setCurrentDate(
        direction === "prev"
          ? addWeeks(currentDate, -1)
          : addWeeks(currentDate, 1)
      );
    } else {
      setCurrentDate(
        direction === "prev"
          ? addDays(currentDate, -1)
          : addDays(currentDate, 1)
      );
    }
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const handleMonthSelect = (monthIndex: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(monthIndex);
    setCurrentDate(newDate);
  };

  const handleYearSelect = (year: number) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(year);
    setCurrentDate(newDate);
  };

  // Get current year and create a range of years (5 years before and after)
  const currentYear = currentDate.getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  return (
    <div className="flex flex-col h-full border rounded-lg overflow-hidden bg-white">
      {/* <div className="flex items-center justify-end p-3">
        <div className="flex items-center space-x-2">
          <ViewToggle
            view={viewMode}
            onViewChange={() => {
              if (viewMode === "calendar") {
                setViewMode("grid"); // Add this line to update local state
                setView && setView("list");
              } else {
                setViewMode("calendar");
                setView && setView("TicketAppoitment");
              }
            }}
            className="h-8"
          />
        </div>
      </div> */}
      <div className="flex items-center justify-between p-3 border-b">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleNavigate("prev")}
            className="h-8 w-8"
          >
            <ChevronLeft className="h-4 w-4 text-black" />
          </Button>

          {/* Replace the span with this Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-8 text-sm font-medium justify-center items-center flex"
              >
                {format(
                  currentDate,
                  calendarView === "month" ? "MMMM yyyy" : "MMM d - MMM d, yyyy"
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2 " align="start">
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-1">
                  {months.map((month, index) => (
                    <Button
                      key={month}
                      variant={
                        currentDate.getMonth() === index
                          ? "secondary"
                          : "outline"
                      }
                      size="sm"
                      className="text-xs h-8"
                      onClick={() => handleMonthSelect(index)}
                    >
                      {month.substring(0, 3)}
                    </Button>
                  ))}
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="grid grid-cols-4 gap-1">
                    {years.map((year) => (
                      <Button
                        key={year}
                        variant={
                          currentDate.getFullYear() === year
                            ? "secondary"
                            : "outline"
                        }
                        size="sm"
                        className="text-xs h-8"
                        onClick={() => handleYearSelect(year)}
                      >
                        {year}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="icon"
            onClick={() => handleNavigate("next")}
            className="h-8 w-8"
          >
            <ChevronRight className="h-4 w-4 text-black" />
          </Button>
        </div>
        <div className="flex items-center">
          <Select
            value={calendarView}
            onValueChange={(value) =>
              handleViewChange(value as CalendarViewType)
            }
          >
            <SelectTrigger className="h-8 w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="week">Week</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {viewMode === "calendar" ? (
          <>
            {calendarView === "month" && (
              <MonthView
                currentDate={currentDate}
                events={normalizedEvents}
                dateRange={dateRange}
                onViewEvent={onViewEvent}
              />
            )}
            {calendarView === "week" && (
              <WeekView
                currentDate={currentDate}
                events={normalizedEvents}
                dateRange={dateRange}
                onViewEvent={onViewEvent}
              />
            )}
          </>
        ) : (
          <TicketTable />
        )}
      </div>
    </div>
  );
}
